import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import {
  groupScheduledByDay,
  joinDetail,
  POSTS_LANE_PAGE,
  PostsBoardPost,
  PostsBoardScheduledRow,
  rolloutDayText,
  scheduledDayHeading,
} from './postsModel';
import { postsMessages } from './postsMessages';
import { PostsLaneProps } from './postsLaneProps';
import { PostsFileRow } from './PostsFileRow';
import { PostsLane } from './PostsLane';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Posts.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PostsScheduledLaneProps extends PostsLaneProps {
  rows: PostsBoardScheduledRow[];
}

/** The chip of a live post: its time, "Draft", "Needs you" or "Posting". */
function StatusChip({ post }: { post: PostsBoardPost }): React.ReactElement {
  const intl = useIntl();
  if (post.status === 'draft') {
    return <span className={`${styles.pill} ${styles.pillScheduled}`}>{intl.formatMessage(postsMessages.draft)}</span>;
  }
  if (post.status === 'needsYou') {
    return <span className={`${styles.pill} ${styles.pillWarn}`}>{intl.formatMessage(postsMessages.needsYou)}</span>;
  }
  if (post.status === 'publishing') {
    return (
      <span className={`${styles.pill} ${styles.pillScheduled}`}>{intl.formatMessage(postsMessages.publishing)}</span>
    );
  }
  return (
    <span className={`${styles.pill} ${styles.pillScheduled}`}>
      {intl.formatTime(new Date(post.scheduledAt), { hour: '2-digit', minute: '2-digit' })}
    </span>
  );
}

/**
 * Scheduled (design assets-rethink c1): one row per draft, scheduled, "needs you" or publishing
 * post, by day. A click on a row opens the post in the Calendar; "Open Calendar" opens the month.
 * A file with a live post is not picked: it cannot be archived ("Cancel the post first").
 */
const PostsScheduledLaneComponent: React.FC<PostsScheduledLaneProps> = (props) => {
  const { rows, songs, now, actions, isHiddenOnPhone } = props;
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);
  const shown = useMemo(() => (isExpanded ? rows : rows.slice(0, POSTS_LANE_PAGE)), [isExpanded, rows]);
  const days = useMemo(() => groupScheduledByDay(shown), [shown]);

  return (
    <PostsLane
      title={intl.formatMessage(postsMessages.laneScheduled)}
      pillClass={styles.pillScheduled}
      count={rows.length}
      headAction={
        <button type="button" className={styles.link} onClick={() => actions.openCalendar()}>
          {intl.formatMessage(postsMessages.openCalendar)}
        </button>
      }
      emptyText={intl.formatMessage(postsMessages.emptyScheduled)}
      hiddenCount={rows.length - shown.length}
      isExpanded={isExpanded}
      onToggleMore={() => setIsExpanded((value) => !value)}
      isHiddenOnPhone={isHiddenOnPhone}
      testId="posts-lane-scheduled"
    >
      {days.map((day) => (
        <React.Fragment key={day.key}>
          <div className={styles.groupHead}>{scheduledDayHeading(intl, day.key, now)}</div>
          {day.rows.map((row) => {
            const song =
              row.file.catalogItemUuid === null
                ? intl.formatMessage(postsMessages.noSong)
                : (songs[row.file.catalogItemUuid]?.title ?? intl.formatMessage(postsMessages.noSong));
            return (
              <PostsFileRow
                key={row.post.postUuid}
                file={row.file}
                detail={joinDetail([
                  row.post.hasCaption ? song : intl.formatMessage(postsMessages.captionNotSet, { song }),
                  rolloutDayText(intl, row.post.rolloutOffsetDays),
                ])}
                platforms={[row.post.platform]}
                selection={null}
                onOpen={() => actions.openCalendar(row.post.postUuid)}
                right={<StatusChip post={row.post} />}
              />
            );
          })}
        </React.Fragment>
      ))}
    </PostsLane>
  );
};

export const PostsScheduledLane = TypedMemo(PostsScheduledLaneComponent);
